import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Wrench, Trash2 } from "lucide-react";

const STATUS_MAP = {
  planifie: "Planifié", en_cours: "En cours", termine: "Terminé", annule: "Annulé",
};

/**
 * SavEditDialog — extrait de CalendarPage pour alléger ce dernier.
 * Le parent garde la maîtrise du state (savForm, employees, etc.).
 */
const SavEditDialog = ({
  open,
  onOpenChange,
  savForm,
  setSavForm,
  editingSav,
  orders,
  subcontractors,
  employees,
  onSave,
  onDelete,
  onToggleEmployee,
}) => {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            <Wrench className="h-5 w-5 text-red-600" />
            {editingSav ? "Modifier le SAV" : "Nouveau SAV"}
          </DialogTitle>
          <DialogDescription>Modifier les informations de l'intervention</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Client *</Label>
              <Input value={savForm.client_name} onChange={e => setSavForm({...savForm, client_name: e.target.value})} data-testid="cal-sav-client" />
            </div>
            <div className="space-y-2">
              <Label>Commande liée</Label>
              <Select value={savForm.order_id} onValueChange={v => setSavForm({...savForm, order_id: v === "_none" ? "" : v})}>
                <SelectTrigger><SelectValue placeholder="Aucune" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="_none">Aucune</SelectItem>
                  {orders.map(o => <SelectItem key={o.id} value={o.id}>{o.reference} - {o.client_name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="md:col-span-2 space-y-2">
              <Label>Description *</Label>
              <Textarea value={savForm.description} onChange={e => setSavForm({...savForm, description: e.target.value})} rows={2} data-testid="cal-sav-desc" />
            </div>
            <div className="space-y-2">
              <Label>Coût (EUR)</Label>
              <Input type="number" step="0.01" value={savForm.cost} onChange={e => setSavForm({...savForm, cost: e.target.value})} />
            </div>
            <div className="space-y-2">
              <Label>Nombre de jours</Label>
              <Input type="number" step="0.5" value={savForm.duration_days} onChange={e => setSavForm({...savForm, duration_days: e.target.value})} />
            </div>
            <div className="space-y-2">
              <Label>Opérateurs</Label>
              <Input type="number" value={savForm.operators} onChange={e => setSavForm({...savForm, operators: e.target.value})} />
            </div>
            <div className="space-y-2">
              <Label>Date d'intervention</Label>
              <Input type="date" value={savForm.intervention_date} onChange={e => setSavForm({...savForm, intervention_date: e.target.value})} />
            </div>
            <div className="space-y-2">
              <Label>Statut</Label>
              <Select value={savForm.status} onValueChange={v => setSavForm({...savForm, status: v})}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(STATUS_MAP).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Sous-traitant</Label>
              <Select value={savForm.subcontractor_id} onValueChange={v => setSavForm({...savForm, subcontractor_id: v === "_none" ? "" : v})}>
                <SelectTrigger><SelectValue placeholder="Aucun" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="_none">Aucun</SelectItem>
                  {subcontractors.map(s => <SelectItem key={s.id} value={s.id}>{s.company_name || `${s.first_name} ${s.last_name}`}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-2">
            <Label>Salariés assignés</Label>
            <div className="border rounded-lg p-3 space-y-2 max-h-40 overflow-y-auto">
              {employees.filter(e => !e.exit_date).map(emp => (
                <div key={emp.id} className="flex items-center gap-2">
                  <Checkbox
                    id={`cal-emp-${emp.id}`}
                    checked={savForm.employee_ids.includes(emp.id)}
                    onCheckedChange={() => onToggleEmployee(emp.id)}
                  />
                  <label htmlFor={`cal-emp-${emp.id}`} className="text-sm cursor-pointer">
                    {emp.first_name} {emp.last_name}
                  </label>
                </div>
              ))}
            </div>
          </div>

          <div className="space-y-2">
            <Label>Notes</Label>
            <Textarea value={savForm.notes} onChange={e => setSavForm({...savForm, notes: e.target.value})} rows={2} />
          </div>
        </div>

        <DialogFooter className="flex justify-between">
          <div>
            {editingSav && (
              <Button variant="destructive" size="sm" onClick={onDelete} data-testid="cal-delete-sav">
                <Trash2 className="h-4 w-4 mr-1" /> Supprimer
              </Button>
            )}
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => onOpenChange(false)}>Annuler</Button>
            <Button onClick={onSave} data-testid="cal-save-sav">Enregistrer</Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default SavEditDialog;
